import { HttpLoaderService, KeyValuePair, ValidationMessage } from '@tymes4-shared';
import { Component, OnInit, Inject } from '@angular/core';
import { FormGroup, Validators, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TenantShop, TenantShopDomainDTO, TenantShopService } from '../../api';
import { RegistrationTypeEnum } from '../../api/model/registrationTypeEnum';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../services/auth.service';

@Component({
    selector: 'app-tenant-shop-form',
    templateUrl: './edit-tenant-shop-form.component.html',
    styleUrls: ['./edit-tenant-shop-form.component.scss'],
    standalone: false
})
export class EditTenantShopComponent implements OnInit {
    public form!: FormGroup;

    public saving = false;
    public tenantShopDomains: Array<TenantShopDomainDTO> | null = null;
    public registrationTypeEnum = Object.keys(RegistrationTypeEnum).map((key, index) => ({ value: index, text: key })).filter(x => x.value !== 0);

    private isNew: boolean = false;
    private editObject: TenantShop | null = null;
    private isVendorAdmin: boolean = false;

    public validationMessages: KeyValuePair<ValidationMessage[]> = {};

    public readonly titleMaxLength = 90;
    public readonly titleRecommendedMin = 30;
    public readonly titleRecommendedMax = 60;
    public readonly descriptionMaxLength = 300;
    public readonly descriptionRecommendedMin = 55;
    public readonly descriptionRecommendedMax = 200;

    constructor(
      @Inject(MAT_DIALOG_DATA) public passedData: any,
      public dialogRef: MatDialogRef<EditTenantShopComponent>,
      private loader: HttpLoaderService,
      private tenantShopService: TenantShopService,
      private translate: TranslateService,
      private authService: AuthService
    ) { }

    ngOnInit() {
      const loggedInUser = this.authService.getLoggedInUser();
      this.isVendorAdmin = loggedInUser?.Roles?.includes('vendor-admin') ?? false;

      this.editObject = this.passedData.payload;
      if (this.editObject == null) {
        this.isNew = true;
        this.editObject = {
          Id: 0,
        };
      }

      this.form = new FormGroup({
          Id: new FormControl(0),
          Name: new FormControl(null, [Validators.required]),
          TenantShopDomainId: new FormControl(null, [Validators.required]),
          IsRegistrationOpenForNewMembers: new FormControl(null),
          RegistrationTypeId: new FormControl(null),
          RegistrationOpenForNewMembers: new FormControl(null),
          AllowAccountActivation: new FormControl(true),
          Title: new FormControl(null, [Validators.maxLength(this.titleMaxLength)]),
          Description: new FormControl(null, [Validators.maxLength(this.descriptionMaxLength)]),
          ImageUrl: new FormControl(null)
      });


      this.tenantShopService.getTenantShopDomains().subscribe((tenantShopDomains: Array<TenantShopDomainDTO>) => {
        this.tenantShopDomains = tenantShopDomains;
        this.form.patchValue(this.editObject);

        this.form.patchValue({
          IsRegistrationOpenForNewMembers: this.editObject.RegistrationOpenForNewMembers,
          RegistrationTypeId: this.registrationTypeEnum.find(x => x.text === this.editObject.RegistrationTypeId)?.value ?? null
        });

        // Disable fields for non-vendor-admin users
        if (!this.isVendorAdmin) {
          this.form.get('Name')?.disable();
          this.form.get('TenantShopDomainId')?.disable();
          this.form.get('AllowAccountActivation')?.disable();
        }
      })
      this.loadData();
    }

    private loadData(): void {
      this.loader.open();

      this.tenantShopService.getTenantShopDomains().subscribe((tenantShopDomains) => {
        this.loader.close();
        this.tenantShopDomains = tenantShopDomains;

        if (this.editObject != null) {
          this.form.patchValue(this.editObject);
          this.form.value.IsRegistrationOpenForNewMembers = this.editObject.RegistrationOpenForNewMembers;
          this.form.patchValue(this.form.value);
        }

        // Disable fields for non-vendor-admin users
        if (!this.isVendorAdmin) {
          this.form.get('Name')?.disable();
          this.form.get('TenantShopDomainId')?.disable();
          this.form.get('AllowAccountActivation')?.disable();
        }
      });
    }

    getTitleCharactersText(): string {
      const length = this.getTitleLength();
      return `${length}/${this.titleMaxLength} ${this.translate.instant('TENANT-SHOPS.EDITDIALOG.FORM.HELP.CHARACTERS')}`;
    }

    getDescriptionCharactersText(): string {
      const length = this.getDescriptionLength();
      return `${length}/${this.descriptionMaxLength} ${this.translate.instant('TENANT-SHOPS.EDITDIALOG.FORM.HELP.CHARACTERS')}`;
    }

    getTitleStatusClass(): string {
      const length = this.getTitleLength();
      if (length === 0) return '';
      if (length >= this.titleRecommendedMin && length <= this.titleRecommendedMax) return 'text-success';
      if (length > this.titleMaxLength) return 'text-danger';
      return 'text-warning';
    }

    getDescriptionStatusClass(): string {
      const length = this.getDescriptionLength();
      if (length === 0) return '';
      if (length >= this.descriptionRecommendedMin && length <= this.descriptionRecommendedMax) return 'text-success';
      if (length > this.descriptionMaxLength) return 'text-danger';
      return 'text-warning';
    }

    submit() {
      const formData = this.isVendorAdmin ? this.form.getRawValue() : this.form.value;
      
      formData.RegistrationOpenForNewMembers = formData.IsRegistrationOpenForNewMembers
        ? formData.RegistrationOpenForNewMembers
        : null;

      this.saving = true;
      this.loader.open();
      if (this.isNew) {
        this.tenantShopService.createTenantShop(formData).subscribe((id: number) => {
          this.dialogRef.close(id);
          this.loader.close();
        })

        return;
      }

      this.tenantShopService.updateTenantShop(formData).subscribe(() => {
        this.dialogRef.close(this.editObject?.Id);
        this.saving = false;
        this.loader.close();
      })
    }

    private getTitleLength(): number {
      return this.form.get('Title')?.value?.length || 0;
    }

    private getDescriptionLength(): number {
      return this.form.get('Description')?.value?.length || 0;
    }
}
